"use client";

import { configureStore } from "@reduxjs/toolkit";
import { useDispatch, useSelector, TypedUseSelectorHook } from "react-redux";
import counterReducer from "@/features/counterSlice";
import authReducer from "@/features/auth/authSlice";
import basketReducer from "@/features/basket/basketSlice";
import affiliateReducer from "@/features/affiliate/affiliateSlice";
import chatReducer from "@/features/chat/chatSlice";
import { Api } from "./api";

// CRITICAL: Import userInjection IMMEDIATELY after importing Api to ensure endpoints are registered synchronously
// This MUST happen before store configuration, ensuring endpoints are available when components use hooks
// This prevents RTK Query hooks from calling hooks conditionally, which violates React's Rules of Hooks
import "@/features/user/userInjection";
import "@/features/company-reviews/companyReviewInjection";
import "@/features/calendar/calendarInjection";
import "@/features/plan/planInjection";
import "@/features/basket/basketApiInjection";
import "@/features/companies-house/companiesHouseApiInjection";
import "@/features/crm/crmApiInjection";

export const store = configureStore({
	reducer: {
		[Api.reducerPath]: Api.reducer,
		counter: counterReducer,
		auth: authReducer,
		basket: basketReducer,
		affiliate: affiliateReducer,
		chat: chatReducer,
	},
	middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(Api.middleware),
	devTools: true,
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;

// Typed hooks for use throughout the app
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
